import { useCallback, useEffect, useRef, useState } from 'react';

import {
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  TextField,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import { UserAddress } from '../../types/user.type';
import { AdminUpdateUserAddressDTO } from '../../types/dtos/admin-update-user.dto';
import AddressAutocomplete from '../../../shared/components/Forms/AddressAutocomplete.component';
import { ParsedAddress, buildAddressLabel } from '../../../shared/components/Forms/parse-google-address.util';
import {
  ADDRESS_COMMENT_LABEL,
  ADDRESS_TITLE_LABEL,
  PLACE_SELECTION_HINT,
  ZIP_CODE_FORMAT_ERROR,
  ZIP_CODE_PATTERN,
} from '../../../shared/components/Forms/address-form.constants';

const EMPTY_ADDRESS: AdminUpdateUserAddressDTO = {
  title: '',
  street: '',
  zipCode: '',
  city: '',
  comment: '',
  isMain: false,
  isBill: false,
};

const buildSavedAddressLabel = (address: UserAddress) =>
  buildAddressLabel({ street: address.street, zipCode: address.zipCode, city: address.city, country: '' });

const toSavedAddress = ({ title, comment, ...address }: AdminUpdateUserAddressDTO): AdminUpdateUserAddressDTO => {
  const trimmedTitle = title?.trim();
  const trimmedComment = comment?.trim();

  return {
    ...address,
    street: address.street.trim(),
    city: address.city.trim(),
    ...(trimmedTitle ? { title: trimmedTitle } : {}),
    ...(trimmedComment ? { comment: trimmedComment } : {}),
  };
};

const useStyles = makeStyles((theme) => ({
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',

    '& .MuiOutlinedInput-root.Mui-focused:not(.Mui-error) .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.admin.primary.main,
    },
    '& .MuiInputLabel-outlined.Mui-focused:not(.Mui-error)': {
      color: theme.palette.admin.primary.main,
    },
  },
  cityRow: {
    display: 'flex',
    gap: '1rem',

    '& > div:first-child': {
      width: '35%',
    },
    '& > div:last-child': {
      width: '65%',
    },
  },
  flags: {
    display: 'flex',
    gap: '1rem',
  },
  saveButton: {
    backgroundColor: theme.palette.admin.primary.main,
    color: theme.palette.admin.primary.contrastText,
    '&:hover': {
      backgroundColor: theme.palette.admin.primary.dark,
    },
  },
  checkbox: {
    '&$checkboxChecked': {
      color: theme.palette.admin.primary.main,
    },
  },
  checkboxChecked: {},
  actions: {
    '& .MuiButton-root': {
      textTransform: 'none',
    },
  },
}));

interface Props {
  isOpen: boolean;
  address: UserAddress | null;
  isSubmitting?: boolean;
  onClose: () => void;
  onSave: (address: AdminUpdateUserAddressDTO) => void;
}

export default function UserAddressDialog({ isOpen, address, isSubmitting = false, onClose, onSave }: Props) {
  const classes = useStyles();

  const [draftAddress, setDraftAddress] = useState<AdminUpdateUserAddressDTO>(EMPTY_ADDRESS);
  const [addressLabel, setAddressLabel] = useState('');
  const [isPlaceSelected, setIsPlaceSelected] = useState(false);
  const lastSelectedAddressLabelRef = useRef<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;

    const initialLabel = address ? buildSavedAddressLabel(address) : '';

    lastSelectedAddressLabelRef.current = address ? initialLabel : null;
    setAddressLabel(initialLabel);
    setIsPlaceSelected(Boolean(address));
    setDraftAddress(address ?? EMPTY_ADDRESS);
  }, [isOpen, address]);

  const applySelectedPlace = useCallback((place: ParsedAddress) => {
    lastSelectedAddressLabelRef.current = buildAddressLabel(place);
    setIsPlaceSelected(true);
    setDraftAddress((previousAddress) => ({
      ...previousAddress,
      street: place.street,
      zipCode: place.zipCode,
      city: place.city,
    }));
  }, []);

  const handleAddressLabelChange = useCallback((value: string) => {
    setAddressLabel(value);

    if (value === lastSelectedAddressLabelRef.current) return;

    lastSelectedAddressLabelRef.current = null;
    setIsPlaceSelected(false);
    setDraftAddress((previousAddress) => ({ ...previousAddress, street: '', zipCode: '', city: '' }));
  }, []);

  const handleChange = (field: keyof AdminUpdateUserAddressDTO, value: string | boolean) =>
    setDraftAddress((previousAddress) => ({ ...previousAddress, [field]: value }));

  const handleClose = () => {
    if (isSubmitting) return;
    onClose();
  };

  const isZipCodeInvalid = draftAddress.zipCode.length > 0 && !ZIP_CODE_PATTERN.test(draftAddress.zipCode);
  const isComplete = Boolean(
    draftAddress.street.trim() && ZIP_CODE_PATTERN.test(draftAddress.zipCode) && draftAddress.city.trim()
  );
  const isPlacePending = addressLabel.length > 0 && !isPlaceSelected;

  const handleSave = () => onSave(toSavedAddress(draftAddress));

  return (
    <Dialog open={isOpen} onClose={handleClose} disableEscapeKeyDown={isSubmitting} fullWidth maxWidth="sm">
      <DialogTitle>{address ? "Modifier l'adresse" : 'Ajouter une adresse'}</DialogTitle>
      <DialogContent className={classes.content}>
        <TextField
          id="user-address-title"
          label={ADDRESS_TITLE_LABEL}
          type="text"
          size="small"
          variant="outlined"
          fullWidth
          disabled={!isPlaceSelected}
          value={draftAddress.title ?? ''}
          onChange={(event) => handleChange('title', event.target.value)}
        />
        <AddressAutocomplete
          id="user-address-search"
          label="Adresse"
          size="small"
          value={addressLabel}
          onChange={handleAddressLabelChange}
          onAddressSelected={applySelectedPlace}
          helperText={isPlacePending ? PLACE_SELECTION_HINT : undefined}
        />
        <TextField
          id="user-address-street"
          label="Rue"
          type="text"
          size="small"
          variant="outlined"
          fullWidth
          required
          disabled={!isPlaceSelected}
          value={draftAddress.street}
          onChange={(event) => handleChange('street', event.target.value)}
        />
        <div className={classes.cityRow}>
          <TextField
            id="user-address-zip-code"
            label="Code postal"
            type="text"
            size="small"
            variant="outlined"
            required
            disabled={!isPlaceSelected}
            error={isZipCodeInvalid}
            helperText={isZipCodeInvalid ? ZIP_CODE_FORMAT_ERROR : undefined}
            inputProps={{ inputMode: 'numeric', maxLength: 5 }}
            value={draftAddress.zipCode}
            onChange={(event) => handleChange('zipCode', event.target.value)}
          />
          <TextField
            id="user-address-city"
            label="Ville"
            type="text"
            size="small"
            variant="outlined"
            fullWidth
            required
            disabled={!isPlaceSelected}
            value={draftAddress.city}
            onChange={(event) => handleChange('city', event.target.value)}
          />
        </div>
        <TextField
          id="user-address-comment"
          label={ADDRESS_COMMENT_LABEL}
          type="text"
          size="small"
          variant="outlined"
          fullWidth
          disabled={!isPlaceSelected}
          multiline
          minRows={2}
          inputProps={{ maxLength: 250 }}
          value={draftAddress.comment ?? ''}
          onChange={(event) => handleChange('comment', event.target.value)}
        />
        <div className={classes.flags}>
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                color="default"
                classes={{ root: classes.checkbox, checked: classes.checkboxChecked }}
                checked={Boolean(draftAddress.isMain)}
                onChange={(event) => handleChange('isMain', event.target.checked)}
              />
            }
            label="Livraison"
          />
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                color="default"
                classes={{ root: classes.checkbox, checked: classes.checkboxChecked }}
                checked={Boolean(draftAddress.isBill)}
                onChange={(event) => handleChange('isBill', event.target.checked)}
              />
            }
            label="Facturation"
          />
        </div>
      </DialogContent>
      <DialogActions className={classes.actions}>
        <Button size="small" variant="outlined" onClick={handleClose} disabled={isSubmitting}>
          Annuler
        </Button>
        <Button
          size="small"
          variant="contained"
          className={classes.saveButton}
          disabled={!isComplete || isSubmitting}
          startIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : null}
          onClick={handleSave}
        >
          {isSubmitting ? 'Enregistrement...' : 'Enregistrer'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
